/**
 * PlanRendererInfoMode — renders the floor plan SVG in Info mode.
 *
 * Responsibilities:
 *  - Mount the cached SVG clone into the container (with per-variant mutations: 3D / color scheme).
 *  - Re-apply color scheme in-place without a full remount on scheme-only changes.
 *
 * All hover, select, and popover interactions are delegated to useInfoModeInteractions.
 */

import { useEffect, useRef, useState, useCallback } from 'react'

import { useApp }     from '../../../../app/AppContext.jsx'
import { useCatalog } from '../../../catalog/index.js'
import { useProduct } from '../../index.js'

import { useInfoModeInteractions } from '../../hooks/useInfoModeInteractions.js'
import { useInfoModePreview }       from '../../hooks/useInfoModePreview.js'
import { usePlanNorthRotation } from '../../hooks/usePlanNorthRotation.js'
import { applyRenderTopMutations }  from '../../utils/svgRenderTop.js'
import { applyColorSchemeMutations, applyColorSchemeHoverHighlight, buildCompareDeciles, getCompareDecile, getBrightnessBucket } from '../../utils/svgColorScheme.js'
import { attachStrokeGhost, removeStrokeGhost } from '../../utils/svgGhostStroke.js'
import { VIEWER_VARIANT } from '../../constants/viewerVariant.js'
import { COLOR_SCHEME } from '../../constants/colorSchemes.js'
import { PRODUCT_VIEW_MODE } from '../../constants/viewModes.js'

import { CompartmentPopover } from './CompartmentPopover.jsx'
import { EquipmentPopover }   from './EquipmentPopover.jsx'
import { InnerWallsPopover }  from './InnerWallsPopover.jsx'
import { OpeningPopover }     from './OpeningPopover.jsx'

import styles from './PlanRendererInfoMode.module.scss'

// CSS class names passed to the hook for direct DOM classList manipulation.
// In 3D: all compartment states use CSS classes → filter brightness (mixin).
// In 2D: CSS class (fill tint) + ghost stroke (outline) are applied together.
const CLASS_NAMES = {
  windowHovered:         styles.windowHovered,
  equipmentHovered:      styles.equipmentHovered,
  partitionHovered:      styles.partitionHovered,
  compartmentHovered:    styles.compartmentHovered,
  compartmentSelected:   styles.compartmentSelected,
  compartmentNotSelected: styles.compartmentNotSelected,
}

/**
 * @param {{ svgElement: SVGElement|null, svgDimensions: { width: number, height: number } }} props
 */
export function PlanRendererInfoMode({ svgElement, svgDimensions }) {
  const containerRef    = useRef(null)
  const mountedSvgRef   = useRef(null)
  const wrapperRef      = useRef(null)
  const hoverGhostRef    = useRef(null)
  const selectedGhostRef = useRef(null)
  const compareDecilesRef = useRef(null)
  const brightnessRef     = useRef(null)

  const { isMobile, panelSettings, containerRef: appRootRef } = useApp()
  const { compartments: compartmentsData, products: allProducts } = useCatalog()
  const { viewerState, viewerActions, selection, capabilities, modes } = useProduct()
  const { selectedCompartmentId, hoveredCompartmentId, viewerVariant, activeColorScheme, rotatedToNorth, infoLegendHoveredKey } = viewerState
  const { setSelectedCompartmentId, setHoveredCompartmentId, setActiveColorScheme, setViewerVariant, setInfoHoveredPathKey } = viewerActions

  // ── Build compare deciles cache (once per allProducts change) ──────────────

  useEffect(() => {
    compareDecilesRef.current = buildCompareDeciles(allProducts ?? [])
  }, [allProducts])

  // ── Build brightness bucket cache (once per product change) ───────────────────

  useEffect(() => {
    const map = new Map()
    for (const c of selection.product?.compartments ?? []) {
      map.set(c.number, getBrightnessBucket(c.window_area ?? 0, c.area_original ?? 0))
    }
    brightnessRef.current = map
  }, [selection.product])

  // ── Derive hover key (path → legend direction) ────────────────────────────
  // Called directly from onPointerMove / onPointerLeave on the plan container.
  // By triggering from pointer events (not from hoveredCompartmentId state), we
  // avoid reacting to external hover sources such as CompartmentTile, which also
  // write to the shared hoveredCompartmentId context state.
  // Skip MONO. BRIGHTNESS/TYPES handled in 5.13.

  const deriveAndSetHoveredKey = useCallback((e) => {
    if (e.pointerType === 'touch' || activeColorScheme === COLOR_SCHEME.MONO) {
      setInfoHoveredPathKey(null)
      return
    }
    const path = e.target?.closest?.('path.compartment') ?? null
    if (!path) {
      setInfoHoveredPathKey(null)
      return
    }
    const nameCad = [...path.classList].find((c) => c.startsWith('id')) ?? null
    const typesMap = compartmentsData?.compartments_types ?? compartmentsData ?? {}
    const typeData = nameCad ? typesMap[nameCad] : null

    if (activeColorScheme === COLOR_SCHEME.ZONES) {
      const location = typeData?.location ?? 'interior'
      const key = location !== 'interior' ? 'outdoor' : (typeData?.zone ?? 'neutral')
      setInfoHoveredPathKey({ scheme: 'zones', key })
    } else if (activeColorScheme === COLOR_SCHEME.COMPARE) {
      const number = Number(path.getAttribute('number'))
      const comp = selection.product?.compartments?.find((c) => c.number === number)
      const area = comp?.area_original ?? null
      if (area != null && nameCad && compareDecilesRef.current) {
        const key = getCompareDecile(area, nameCad, compareDecilesRef.current)
        setInfoHoveredPathKey({ scheme: 'compare', key })
      } else {
        setInfoHoveredPathKey(null)
      }
    } else if (activeColorScheme === COLOR_SCHEME.BRIGHTNESS) {
      const number = Number(path.getAttribute('number'))
      const bucket = brightnessRef.current?.get(number)
      setInfoHoveredPathKey(bucket !== undefined ? { scheme: COLOR_SCHEME.BRIGHTNESS, key: bucket } : null)
    } else {
      // TYPES — no legend sync needed
      setInfoHoveredPathKey(null)
    }
  }, [activeColorScheme, compartmentsData, selection.product, setInfoHoveredPathKey])

  // ── On-entry preview (color scheme carousel) ──────────────────────────────
  // Plays once per session — cycles non-MONO schemes then settles back on MONO.

  const [previewInteracted, setPreviewInteracted] = useState(false)

  // ── Rotate-to-North ───────────────────────────────────────────────────────

  const { wrapperStyle, rendererStyle } = usePlanNorthRotation({
    rotatedToNorth,
    azimuth: selection.product?.azimuth ?? null,
    svgDimensions,
    wrapperRef,
  })

  // Force 2D during the preview so color-scheme mutations are visible.
  // Exception: if the user explicitly configured Info mode as the default start
  // AND the viewer opened in 3D (Info+3D is their intended experience).
  const isInfoDefault3DStart =
    modes.defaultMode === PRODUCT_VIEW_MODE.INFO && viewerVariant === VIEWER_VARIANT._3D
  const shouldForce2DForPreview = viewerVariant === VIEWER_VARIANT._3D && !isInfoDefault3DStart

  useInfoModePreview({
    availableColorSchemes: capabilities.availableColorSchemes,
    monoScheme: COLOR_SCHEME.MONO,
    enabled: !!svgElement,
    userInteracted: previewInteracted,
    setActiveColorScheme,
    force2DVariant:    shouldForce2DForPreview ? VIEWER_VARIANT._2D : null,
    restoreVariant:    shouldForce2DForPreview ? VIEWER_VARIANT._3D : null,
    setViewerVariant:  shouldForce2DForPreview ? setViewerVariant  : null,
  })

  // Keeps the mount effect from re-cloning on every scheme switch.
  // The patch effect below handles scheme-only changes via in-place mutation.
  const activeColorSchemeRef = useRef(activeColorScheme)
  activeColorSchemeRef.current = activeColorScheme

  // ── Effective popover enabled flags ───────────────────────────────────────

  const ps           = panelSettings?.unit_sheet_mode_info?.compartment_popover
  const psOpenings   = panelSettings?.unit_sheet_mode_info?.openings_popover
  const psEquipment  = panelSettings?.unit_sheet_mode_info?.equipment_popover
  const psInnerWalls = panelSettings?.unit_sheet_mode_info?.inner_walls_popover

  const compartmentPopoverEnabled = ps?.is_visible          ?? false
  const openingsPopoverEnabled    = psOpenings?.is_visible  ?? false
  const equipmentPopoverEnabled   = psEquipment?.is_visible ?? false
  const innerWallsPopoverEnabled  = psInnerWalls?.is_visible ?? false

  // Per-field visibility for the compartment popover, gated by live capabilities.
  // Insolation fields are additionally gated by whether the product has insolation data.
  const popoverSettings = compartmentPopoverEnabled
    ? {
        showArea:            ps?.area              ?? true,
        showExposition:      ps?.exposition        ?? true,
        showInsolationRange: (ps?.insolation_range ?? true) && capabilities.hasInsolationData,
        showInsolationHours: (ps?.insolation_hours ?? true) && capabilities.hasInsolationData,
        showWindowsRatio:    ps?.windows_ratio     ?? true,
        showWindowsArea:     ps?.windows_area      ?? true,
        showZone:            ps?.zone              ?? true,
      }
    : null

  // ── Mount / update SVG ────────────────────────────────────────────────────

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Clear previous clone (hover-tracking refs are reset inside the hook's own effect)
    container.innerHTML = ''
    if (!svgElement) return

    // Clone so this renderer's mutations don't corrupt the cached canonical element
    const clone = svgElement.cloneNode(true)

    if (viewerVariant === VIEWER_VARIANT._3D) {
      applyRenderTopMutations(clone)
    } else {
      applyColorSchemeMutations(
        clone,
        activeColorSchemeRef.current,
        compartmentsData,
        selection.product?.compartments ?? [],
        allProducts ?? [],
        appRootRef.current,
      )
    }

    container.appendChild(clone)
  }, [allProducts, appRootRef, compartmentsData, selection.product, svgElement, viewerVariant])

  // ── Re-apply color scheme without full SVG remount ────────────────────────
  // When only activeColorScheme changes (svgElement unchanged), mutate the
  // already-mounted clone in place to avoid a flash + scroll-reset.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // Skip patch in 3D — pattern fills managed by applyRenderTopMutations
    if (viewerVariant === VIEWER_VARIANT._3D) return
    // Only patch in place when the mounted clone belongs to the current svgElement
    if (mountedSvgRef.current !== svgElement) {
      mountedSvgRef.current = svgElement
      return
    }
    const mountedSvg = container.querySelector('svg')
    if (!mountedSvg) return
    applyColorSchemeMutations(
      mountedSvg,
      activeColorScheme,
      compartmentsData,
      selection.product?.compartments ?? [],
      allProducts ?? [],
      appRootRef.current,
    )
  }, [activeColorScheme]) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: only re-patch on scheme change

  // ── Interactions (hover / select / popovers) ──────────────────────────────

  const { handlePointerMove, handlePointerLeave, handleClick, popoverState } =
    useInfoModeInteractions({
      svgElement,
      viewerVariant,
      compartmentPopoverEnabled,
      openingsPopoverEnabled,
      equipmentPopoverEnabled,
      innerWallsPopoverEnabled,
      product: selection.product,
      isMobile,
      selectedCompartmentId,
      setSelectedCompartmentId,
      setHoveredCompartmentId,
      containerRef,
      classNames: CLASS_NAMES,
    })

  // ── Hover (2D: ghost stroke / 3D: CSS class brightness) ─────────────────

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // Clear previous hover
    container.querySelectorAll('path.compartment').forEach((p) => {
      p.classList.remove(CLASS_NAMES.compartmentHovered)
    })
    removeStrokeGhost(hoverGhostRef)
    if (hoveredCompartmentId == null) return
    const path = container.querySelector(`path.compartment[number="${hoveredCompartmentId}"]`)
    if (!path) return
    if (viewerVariant === VIEWER_VARIANT._3D) {
      path.classList.add(CLASS_NAMES.compartmentHovered)
    } else {
      // Fill tint via CSS class (applies for MONO; inline fill wins for other schemes)
      path.classList.add(CLASS_NAMES.compartmentHovered)
      attachStrokeGhost(path, 'var(--bim-color-product-outline, var(--bim-color-hero-outlines))', '0.75em', hoverGhostRef)
    }
  }, [hoveredCompartmentId, svgElement, viewerVariant])

  // ── Selected (2D: ghost stroke / 3D: CSS class brightness + dim others) ──

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // Clear previous selected state
    container.querySelectorAll('path.compartment').forEach((p) => {
      p.classList.remove(CLASS_NAMES.compartmentSelected, CLASS_NAMES.compartmentNotSelected)
    })
    removeStrokeGhost(selectedGhostRef)
    if (selectedCompartmentId == null) return
    const path = container.querySelector(`path.compartment[number="${selectedCompartmentId}"]`)
    if (!path) return
    if (viewerVariant === VIEWER_VARIANT._3D) {
      path.classList.add(CLASS_NAMES.compartmentSelected)
      container.querySelectorAll(`path.compartment:not([number="${selectedCompartmentId}"])`).forEach((p) => {
        p.classList.add(CLASS_NAMES.compartmentNotSelected)
      })
    } else {
      // Fill tint via CSS class (applies for MONO; inline fill wins for other schemes)
      path.classList.add(CLASS_NAMES.compartmentSelected)
      attachStrokeGhost(path, 'var(--bim-color-product-active, var(--bim-color-hero-active))', '1em', selectedGhostRef)
    }
  }, [selectedCompartmentId, svgElement, viewerVariant])

  // ── Legend hover → paths (legend→path direction) ──────────────────────────
  // Dims non-matching compartments when a legend swatch is hovered.
  // 2D: inline fill mutation via applyColorSchemeHoverHighlight (reads dataset.schemeFill).
  // 3D: brightness filter via compartmentNotSelected class.
  // Skip MONO. BRIGHTNESS/TYPES handled in 5.13.

  useEffect(() => {
    if (activeColorScheme === COLOR_SCHEME.MONO) return
    const container = containerRef.current
    if (!container) return
    const mountedSvg = container.querySelector('svg')
    if (!mountedSvg) return

    const is3D = viewerVariant === VIEWER_VARIANT._3D
    const key  = infoLegendHoveredKey?.scheme === activeColorScheme ? infoLegendHoveredKey.key : null

    if (key == null) {
      if (!is3D) {
        if (activeColorScheme === COLOR_SCHEME.BRIGHTNESS) {
          mountedSvg.querySelectorAll('path.compartment').forEach((p) => { p.style.filter = '' })
        } else {
          applyColorSchemeHoverHighlight(mountedSvg, activeColorScheme, null)
        }
      } else {
        // Restore: clear legend dim, then re-apply selection dim if a compartment is selected
        mountedSvg.querySelectorAll('path.compartment').forEach((p) => {
          p.classList.remove(CLASS_NAMES.compartmentNotSelected)
        })
        if (selectedCompartmentId != null) {
          mountedSvg.querySelectorAll(`path.compartment:not([number="${selectedCompartmentId}"])`).forEach((p) => {
            p.classList.add(CLASS_NAMES.compartmentNotSelected)
          })
        }
      }
      return
    }

    // Compute matching compartment numbers for the hovered legend key
    const typesMap = compartmentsData?.compartments_types ?? compartmentsData ?? {}
    const productCompartments = selection.product?.compartments ?? []
    const matchingNumbers = new Set()

    if (activeColorScheme === COLOR_SCHEME.ZONES) {
      for (const comp of productCompartments) {
        const typeData = typesMap[comp.name_cad] ?? null
        const location = typeData?.location ?? 'interior'
        const zoneKey  = (location !== 'interior') ? 'outdoor' : (typeData?.zone ?? 'neutral')
        if (zoneKey === key) matchingNumbers.add(comp.number)
      }
    } else if (activeColorScheme === COLOR_SCHEME.COMPARE) {
      if (compareDecilesRef.current) {
        for (const comp of productCompartments) {
          if (comp.area_original == null || !comp.name_cad) continue
          const decile = getCompareDecile(comp.area_original, comp.name_cad, compareDecilesRef.current)
          if (decile === key) matchingNumbers.add(comp.number)
        }
      }
    } else if (activeColorScheme === COLOR_SCHEME.BRIGHTNESS) {
      if (brightnessRef.current) {
        for (const comp of productCompartments) {
          const bucket = brightnessRef.current.get(comp.number)
          if (bucket !== undefined && bucket === key) matchingNumbers.add(comp.number)
        }
      }
    } else {
      // TYPES — no legend→path sync
      return
    }

    if (!is3D) {
      if (activeColorScheme === COLOR_SCHEME.BRIGHTNESS) {
        // BRIGHTNESS fills are hex (not var()), use filter-based dimming instead
        mountedSvg.querySelectorAll('path.compartment').forEach((p) => {
          const n = Number(p.getAttribute('number'))
          p.style.filter = matchingNumbers.has(n) ? '' : 'saturate(0.2) brightness(1.1)'
        })
      } else {
        applyColorSchemeHoverHighlight(mountedSvg, activeColorScheme, matchingNumbers)
      }
    } else {
      mountedSvg.querySelectorAll('path.compartment').forEach((p) => {
        const n = Number(p.getAttribute('number'))
        if (matchingNumbers.has(n)) {
          p.classList.remove(CLASS_NAMES.compartmentNotSelected)
        } else {
          p.classList.add(CLASS_NAMES.compartmentNotSelected)
        }
      })
    }
  }, [infoLegendHoveredKey, svgElement, activeColorScheme, viewerVariant, selectedCompartmentId]) // eslint-disable-line react-hooks/exhaustive-deps -- containerRef/compartmentsData/selection.product stable, compareDecilesRef is a ref

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div ref={wrapperRef} className={styles.wrapper} style={wrapperStyle}>
        <div
          ref={containerRef}
          className={`${styles.renderer} bim-plan`}
          data-variant={viewerVariant}
          style={rendererStyle}
          onPointerMove={(e) => { if (!previewInteracted) setPreviewInteracted(true); handlePointerMove(e); deriveAndSetHoveredKey(e) }}
          onPointerLeave={(e) => { handlePointerLeave(e); setInfoHoveredPathKey(null) }}
          onClick={(e) => { if (!previewInteracted) setPreviewInteracted(true); handleClick(e) }}
          aria-label="Rzut lokalu"
        />
      </div>
      {popoverState.compartment.enabled && (
        <CompartmentPopover
          compartment={popoverState.compartment.data}
          position={popoverState.compartment.position}
          isMobile={isMobile}
          isOpen={popoverState.compartment.isOpen}
          onClose={popoverState.compartment.onClose}
          settings={popoverSettings}
          compartmentTypes={compartmentsData}
        />
      )}
      {popoverState.window.enabled && (
        <OpeningPopover
          window={popoverState.window.data}
          position={popoverState.window.position}
          isMobile={isMobile}
          isOpen={popoverState.window.isOpen}
          onClose={popoverState.window.onClose}
        />
      )}
      {popoverState.equipment.enabled && (
        <EquipmentPopover
          equipmentId={popoverState.equipment.id}
          position={popoverState.equipment.position}
          scale={popoverState.equipment.scale}
          isMobile={isMobile}
          isOpen={popoverState.equipment.isOpen}
          onClose={popoverState.equipment.onClose}
        />
      )}
      {popoverState.partition.enabled && (
        <InnerWallsPopover
          areaWalls={selection.product?.area_walls ?? null}
          position={popoverState.partition.position}
          isMobile={isMobile}
          isOpen={popoverState.partition.isOpen}
          onClose={popoverState.partition.onClose}
        />
      )}
    </>
  )
}
